import Link from 'next/link'
import { useRouter } from 'next/router';

import style from './breadcrumbs.module.scss';
import { config } from '../../../../config';
import { Themes } from '@layouts/main-layout/themes.enum';

interface BreadcrumbsProps {
    lastElementTitle?: string;
    theme?: Themes;
}

interface IBreadcrumbsStructureData {
    '@context': 'https://schema.org';
    '@type': 'BreadcrumbList';
    itemListElement: IItemListElement[];
}

interface IItemListElement {
    '@type': 'ListItem';
    position: number;
    item: IItemListElementItem;
}

interface IItemListElementItem {
    '@id': string;
    name: string;
}

const PATH_TO_TITLE: Map<string, string> = new Map([
    ['', 'Home'],
    ['ui-ux-design', 'UI/UX Design'],
    ['sdlc', 'SDLC'],
    ['mvp-development', 'MVP Development'],
    ['qa-services', 'Software QA Services'],
    ['mobile-development', 'Mobile app development'],
    ['web-development', 'Web development services'],
    ['qa-testing', 'QA testing'],
    ['discovery', 'Discovery Phase'],
    ['success-stories', 'Success Stories'],
    ['dedicated-teams', 'Dedicated Teams'],
    ['staff-augmentation', 'Staff Augmentation'],
]);

const Breadcrumbs = ({ lastElementTitle, theme }: BreadcrumbsProps) => {
    const router = useRouter();
    const [path] = router.asPath.split('?');
    const pathSteps = path.split('/');
    const lastIndex = pathSteps.length - 1;
    let fullPath = '';
    let fullBreadcrumbsPath = '';

    const steps = () => {
        return pathSteps.map((item: string, index: number) => {
            const isLastIndex = index === lastIndex;
            fullPath = `${fullPath}${item}/`;
            return (
                <div className={isLastIndex ? `${style.step} ${style.step_disabled}` : style.step} key={index}>
                    <Link href={fullPath}>
                        <a className={style.step__link}>
                            {stepTitle(item, isLastIndex)}
                        </a>
                    </Link>
                    <div className={style.step__arrow} />
                </div>
            )
        })
    }
    const stepTitle = (element: string, isLastStep: boolean) => {
        if (isLastStep && lastElementTitle) {
            return lastElementTitle;
        }
        return (PATH_TO_TITLE.get(element) || element).replace(/-/g, ' ');
    }

    const capitalize = (value: string): string => {
        if (!value) return '';
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    const breadcrumbsStructureData: IBreadcrumbsStructureData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: pathSteps.map((item: string, index: number) => {
            const isLastIndex: boolean = index === lastIndex;
            const isHomeLink: boolean = !index;
            fullBreadcrumbsPath = `${fullBreadcrumbsPath}${item}/`;
            return {
                '@type': 'ListItem',
                position: index + 1,
                item: {
                    '@id': config.HOST + fullBreadcrumbsPath,
                    name: isHomeLink ? 'Agilie' : capitalize(stepTitle(item, isLastIndex))
                }
            }
        })
    };

    if (!theme) {
        theme = Themes.Default;
    }

    return (
      <div className={`${style.wrapper} ${style[theme]}`}>
        <div className={style.step__buttons}>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(breadcrumbsStructureData)}}
            />

            {
                steps()
            }
        </div>
      </div>
    );
}

export default Breadcrumbs;
